import React, { useState } from "react";
import {
  Button,
  Card,
  Divider,
  message,
  Select,
  Space,
  Table,
  Tag,
} from "antd";
import { ReloadOutlined, DollarOutlined } from "@ant-design/icons";
import { useGetClientsWithActiveStorage } from "~/services/client/client.queries";
import { useInventoryBillingInvoices } from "~/services/inventory-billing/inventory-billing.queries";
import { useTriggerBillingChecks } from "~/services/inventory-billing";
import {
  getJobsStatus,
  processMonthlyStorageInvoices,
  triggerDailySnapshotsJob,
  triggerInvoiceSyncJob,
  triggerMonthlyInvoicesJob,
  triggerStorageCalculationsJob,
} from "~/services/storage/storage.api";
import { HistoryFilters, JobType } from "../types";
import { JobStatusCard } from "./JobStatusCard";

const { Option } = Select;

export const BillingHistoryTab: React.FC = () => {
  const [historyFilters, setHistoryFilters] = useState<HistoryFilters>({
    page: 1,
    limit: 10,
  });
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);

  // React Query hooks
  const { data: clients } = useGetClientsWithActiveStorage();
  const {
    data: inventoryBillingInvoices,
    isLoading: isLoadingInvoices,
    refetch: refetchInvoices,
  } = useInventoryBillingInvoices(historyFilters);
  const triggerBillingChecksMutation = useTriggerBillingChecks();

  // Process all monthly storage invoices
  const handleProcessAllInvoices = async () => {
    try {
      setIsProcessing(true);
      const response = await processMonthlyStorageInvoices();
      message.success(
        `Processed ${response.processed} storage invoices successfully`
      );
      refetchInvoices();
    } catch (error) {
      console.error("Error processing storage invoices:", error);
      message.error("Failed to process storage invoices");
    } finally {
      setIsProcessing(false);
    }
  };

  // Load jobs status
  const loadJobsStatus = async () => {
    try {
      setIsLoadingJobs(true);
      const status = await getJobsStatus();
      console.log("Jobs status:", status);
    } catch (error) {
      console.error("Error loading jobs status:", error);
      message.error("Failed to load jobs status");
    } finally {
      setIsLoadingJobs(false);
    }
  };

  // Trigger job handlers
  const handleTriggerJob = async (jobType: JobType) => {
    try {
      setIsProcessing(true);
      let response;

      switch (jobType) {
        case "snapshots":
          response = await triggerDailySnapshotsJob();
          break;
        case "invoices":
          response = await triggerMonthlyInvoicesJob();
          break;
        case "sync":
          response = await triggerInvoiceSyncJob();
          break;
        case "calculations":
          response = await triggerStorageCalculationsJob();
          break;
        case "billing-checks":
          response = await triggerBillingChecksMutation.mutateAsync();
          message.success(
            `Billing checks completed: ${response.itemsStartedBilling} items started billing, ${response.itemsTransitioned} items transitioned, ${response.invoicesCreated} invoices created`
          );
          refetchInvoices();
          return;
      }

      message.success(
        response.message || `${jobType} job triggered successfully`
      );
      refetchInvoices();
      loadJobsStatus();
    } catch (error) {
      console.error(`Error triggering ${jobType} job:`, error);
      message.error(`Failed to trigger ${jobType} job`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFiltersChange = (filters: HistoryFilters) => {
    setHistoryFilters(filters);
  };

  const historyColumns = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      render: (invoiceNumber: string) => invoiceNumber || "N/A",
    },
    {
      title: "Client",
      dataIndex: ["client", "companyName"],
      key: "client",
      render: (companyName: string) => companyName || "N/A",
    },
    {
      title: "Inventory",
      key: "inventory",
      render: (record: any) => {
        const ref = record.inventory?.inventoryRef || "N/A";
        const title = record.inventory?.title || "N/A";
        return `${ref} - ${title}`;
      },
    },
    {
      title: "Billing Period",
      key: "billingPeriod",
      render: (record: any) => {
        const startDate = new Date(
          record.billingPeriod?.startDate
        ).toLocaleDateString();
        const endDate = record.billingPeriod?.endDate
          ? new Date(record.billingPeriod.endDate).toLocaleDateString()
          : "Ongoing";
        return `${startDate} - ${endDate}`;
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount: any) => {
        const numAmount =
          typeof amount === "number" ? amount : parseFloat(amount) || 0;
        return `$${numAmount.toFixed(2)}`;
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const statusConfig = {
          draft: { color: "orange", text: "Draft" },
          pending: { color: "blue", text: "Pending" },
          sent: { color: "purple", text: "Sent" },
          paid: { color: "green", text: "Paid" },
          overdue: { color: "red", text: "Overdue" },
          cancelled: { color: "gray", text: "Cancelled" },
        };
        const config =
          statusConfig[status as keyof typeof statusConfig] ||
          statusConfig.pending;
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      render: (description: string) => description || "N/A",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Process All Invoices Button */}
      <div className="flex justify-between items-center">
        <div></div>
        <div>
          <Button
            type="primary"
            icon={<DollarOutlined />}
            onClick={handleProcessAllInvoices}
            loading={isProcessing}
          >
            Process All Monthly Invoices
          </Button>
        </div>
      </div>

      <Card
        title="Inventory Billing Invoices"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => refetchInvoices()}
              loading={isLoadingInvoices}
            >
              Refresh
            </Button>
          </Space>
        }
      >
        {/* Filter Controls */}
        <div className="mb-4 flex flex-wrap gap-4">
          <Select
            placeholder="Filter by client"
            style={{ width: 200 }}
            allowClear
            onChange={(value) =>
              handleFiltersChange({ ...historyFilters, clientId: value })
            }
          >
            {clients?.map((client) => (
              <Option key={client.id} value={client.id}>
                {client.companyName}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Filter by year"
            style={{ width: 120 }}
            allowClear
            onChange={(value) =>
              handleFiltersChange({ ...historyFilters, year: value })
            }
          >
            {Array.from(
              { length: 5 },
              (_, i) => new Date().getFullYear() - i
            ).map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>

          <Select
            placeholder="Filter by month"
            style={{ width: 150 }}
            allowClear
            onChange={(value) =>
              handleFiltersChange({ ...historyFilters, month: value })
            }
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((month) => (
              <Option key={month} value={month}>
                {new Date(0, month - 1).toLocaleString("default", {
                  month: "long",
                })}
              </Option>
            ))}
          </Select>
        </div>

        <Table
          dataSource={inventoryBillingInvoices?.invoices || []}
          columns={historyColumns}
          rowKey="id"
          loading={isLoadingInvoices}
          pagination={{
            current: historyFilters.page,
            pageSize: historyFilters.limit,
            total: inventoryBillingInvoices?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} invoices`,
            onChange: (page, pageSize) => {
              handleFiltersChange({
                ...historyFilters,
                page,
                limit: pageSize,
              });
            },
          }}
        />
      </Card>

      <Divider />

      {/* Job Management Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <JobStatusCard
          title="Daily Snapshots"
          description="Create storage snapshots for all clients"
          schedule="Runs daily at 1:00 AM"
          jobType="snapshots"
          onTrigger={handleTriggerJob}
          isLoading={isProcessing}
        />
        <JobStatusCard
          title="Monthly Invoices"
          description="Generate monthly storage invoices"
          schedule="Runs 1st of month at 9:00 AM"
          jobType="invoices"
          onTrigger={handleTriggerJob}
          isLoading={isProcessing}
        />
        <JobStatusCard
          title="Invoice Sync"
          description="Sync invoices with external systems"
          schedule="Runs every 6 hours"
          jobType="sync"
          onTrigger={handleTriggerJob}
          isLoading={isProcessing}
        />
        <JobStatusCard
          title="Storage Calculations"
          description="Recalculate storage amounts"
          schedule="Runs daily at 11:00 PM"
          jobType="calculations"
          onTrigger={handleTriggerJob}
          isLoading={isProcessing}
        />
        <JobStatusCard
          title="Billing Checks"
          description="Check and process billing transitions"
          schedule="Runs daily at 1:00 AM"
          jobType="billing-checks"
          onTrigger={handleTriggerJob}
          isLoading={isProcessing}
        />
      </div>

      <div className="flex justify-center">
        <Button
          onClick={loadJobsStatus}
          loading={isLoadingJobs}
          icon={<ReloadOutlined />}
        >
          Load Jobs Status
        </Button>
      </div>
    </div>
  );
};
